import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SingleSelectFieldFormModalData, SingleSelectOptionValue } from 'src/app/interface/components/forms/single-select-field-form.interface';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'common-single-select-field-form-modal',
  templateUrl: './single-select-field-form-modal.component.html',
  styleUrls: ['./single-select-field-form-modal.component.scss']
})
export class SingleSelectFieldFormModalComponent<T extends SingleSelectOptionValue = SingleSelectOptionValue> implements OnInit {
  @Input() title: string = '';
  @Input() inputLabel: string = '';
  @Input() inputHint: string = '';
  @Input() options: T[] = [];
  @Input() selectedValue: T = null;
  @Input() labelButtonCancel: string = 'maia.doors.button.cancel';
  @Input() labelButtonSave: string = 'maia.doors.button.save';
  @Input() displayProperty: string = '';
  @Input() displayFunction: (item: T) => string = null;
  @Input() isObjectMode: boolean = false;
  @Input() loading: boolean = false;
  @Input() clearValue?: T;

  @Output() save = new EventEmitter<T>();
  @Output() cancel = new EventEmitter<void>();

  selectedOption: T = null;
  checkboxControl?: FormControl;
  checkboxOption?: SingleSelectFieldFormModalData<T>['checkboxOption'];
  private lastEnabledValue: T = null;
  selectDisabled = false;

  constructor(
    private matDialogRef: MatDialogRef<SingleSelectFieldFormModalComponent<T>>,
    @Inject(MAT_DIALOG_DATA) public data: SingleSelectFieldFormModalData<T>
  ) {
    // Inicializar
    if (this.data) {
      this.title = this.data.title || this.title;
      this.inputLabel = this.data.inputLabel || this.inputLabel;
      this.inputHint = this.data.inputHint || this.inputHint;
      this.options = this.data.options || this.options;
      this.selectedValue =
        this.data.selectedValue != null ? this.data.selectedValue : this.selectedValue;
      this.clearValue = this.data.clearValue !== undefined ? this.data.clearValue : this.clearValue;
      this.labelButtonCancel = this.data.labelButtonCancel || this.labelButtonCancel;
      this.labelButtonSave = this.data.labelButtonSave || this.labelButtonSave;
      this.displayProperty = this.data.displayProperty || this.displayProperty;
      this.displayFunction = this.data.displayFunction || this.displayFunction;
      this.isObjectMode = this.data.isObjectMode !== undefined ? this.data.isObjectMode : this.isObjectMode;
      this.checkboxOption = this.data.checkboxOption;
    }

    // Inicializar valor seleccionado
    this.selectedOption = this.selectedValue;
    this.lastEnabledValue = this.selectedValue;

    if (this.checkboxOption) {
      const initialChecked = !!this.checkboxOption.checkedByDefault;
      this.checkboxControl = new FormControl(initialChecked);
      if (initialChecked) {
        this.applyCheckboxState(true);
      }
      this.checkboxControl.valueChanges.subscribe((checked: any) => {
        this.applyCheckboxState(Boolean(checked));
      });
    }
  }

  ngOnInit(): void {}


  getDisplayValue(item: any): string {
    if (this.displayFunction) {
      return this.displayFunction(item);
    }
    
    if (this.isObjectMode && this.displayProperty && item != null && typeof item === 'object') {
      return String((item as Record<string, unknown>)[this.displayProperty] ?? '');
    }

    return item != null && item !== '' ? String(item) : '';
  }

  // Métodos para el select-outline
  valueFn = (option: any): any => {
    return option;
  }

  displayFn = (option: any): string => {
    return this.getDisplayValue(option);
  }

  onOptionSelected(selectedValue: unknown): void {
    if (this.selectDisabled) {
      return;
    }
    const resolved = this._resolveSelectedValue(selectedValue);
    this.selectedOption = resolved;
    this.lastEnabledValue = resolved;
  }

  private _resolveSelectedValue(selectedValue: unknown): T {
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return (this.clearValue !== undefined ? this.clearValue : selectedValue) as T;
    }
    return selectedValue as T;
  }

  private applyCheckboxState(checked: boolean): void {
    const forced = (this.checkboxOption?.forcedValue ?? null) as T;
    if (checked) {
      this.selectDisabled = true;
      this.selectedOption = forced;
    } else {
      this.selectDisabled = false;
      this.selectedOption = this.lastEnabledValue;
    }
  }

  onEventClose(): void {
    this.matDialogRef.close();
  }

  onEventCancel(): void {
    this.cancel.emit();
    this.matDialogRef.close();
  }

  /**
   * Permite `0` (p. ej. "Sin mantenimiento" en UI), `false`, `''` y `null` cuando `clearValue === null`.
   */
  get canSave(): boolean {
    if (this.selectedOption === undefined) {
      return false;
    }
    if (this.selectedOption === null) {
      return this.clearValue === null;
    }
    return true;
  }

  onEventSave(): void {
    if (!this.canSave) {
      return;
    }
    this.save.emit(this.selectedOption);
    this.matDialogRef.close(this.selectedOption);
  }
}
